/**
 * jogos.ts — Lista de jogos exibidos no hub (FALLBACK).
 *
 * A fonte de verdade e o catalogo dinamico do monolito (jogo_catalogo,
 * ?a=jogos.api&op=catalogo — migr 300). Esta lista renderiza na hora
 * (zero flicker de rede) e e substituida quando a API responde.
 * Mudou la, muda aqui — e vice-versa.
 */

export type StatusJogo = 'estavel' | 'beta' | 'em-breve';

export interface Jogo {
  id: string;
  titulo: string;
  descricao: string;
  url: string;             // pode ser path relativo (/logic-bugs/) ou absoluto
  status: StatusJogo;
  icone: string;           // emoji ou caractere unicode (reserva, se nao houver capa)
  gradiente: string;       // CSS gradient pro thumb (fundo enquanto a capa carrega)
  capa?: string;           // imagem da capa (a tela real do jogo) — ver CAPAS
  meta?: Array<{ icone: string; texto: string }>;
}

/**
 * CAPAS — a imagem de cada cartao, por slug.
 *
 * A arte NAO vem do banco: o catalogo (jogo_catalogo) manda texto, ordem e
 * status; a imagem e' do hub, versionada em public/thumbs/ e gerada pelos
 * scripts/capas-*.  Slug sem capa aqui cai no emoji + gradiente de sempre.
 */
export const CAPAS: Record<string, string> = {
  'phet': '/thumbs/phet.webp',
  'atividades': '/thumbs/atividades.webp',
  'logic-bugs': '/thumbs/logic-bugs.webp',
  'spacewar': '/thumbs/spacewar.webp',
  'digitacao': '/thumbs/digitacao.webp',
  'encceja-praticar': '/thumbs/encceja-praticar.webp',
  'programar': '/thumbs/programar.webp',
  'exercicios': '/thumbs/exercicios.webp',
  'ferramentas': '/thumbs/ferramentas.webp',
};

export const JOGOS: Jogo[] = [
  {
    id: 'phet',
    titulo: 'Laboratório de Simulações',
    descricao:
      'Simulacoes interativas de matematica, fisica, quimica, biologia e eletricidade. Mexa nos controles, erre sem medo e veja a resposta na hora.',
    url: '/sims/',
    status: 'estavel',
    icone: '🧪',
    gradiente: 'linear-gradient(135deg, #7c3aed 0%, #2e1065 100%)',
    capa: CAPAS['phet'],
    meta: [
      { icone: '🎯', texto: '157 simulações' },
      { icone: '🎓', texto: 'ENCCEJA + cursos técnicos' },
    ],
  },
  {
    id: 'atividades',
    titulo: 'Atividades',
    descricao:
      'Atividades educativas de matematica e linguagem para criancas. Soma, subtracao, multiplicacao, divisao, memoria, contagem, letras e frases.',
    url: '/atividades/',
    status: 'estavel',
    icone: '🧮',
    gradiente: 'linear-gradient(135deg, #f59e0b 0%, #7c2d12 100%)',
    capa: CAPAS['atividades'],
    meta: [
      { icone: '🎯', texto: '59 atividades' },
      { icone: '👶', texto: '4–12 anos' },
    ],
  },
  {
    id: 'logic-bugs',
    titulo: 'Logic Bugs',
    descricao:
      '126 fases de puzzle 2D em grade. Insetos engenheiros, blocos pra empurrar, chaves coloridas e dinamite.',
    url: '/logic-bugs/',
    status: 'estavel',
    icone: '🪲',
    gradiente: 'linear-gradient(135deg, #1f3a26 0%, #0d2114 100%)',
    capa: CAPAS['logic-bugs'],
    meta: [
      { icone: '🎯', texto: '126 fases' },
      { icone: '👥', texto: 'Single-player' },
    ],
  },
  {
    id: 'spacewar',
    titulo: 'SpaceWar',
    descricao:
      'Estrategia em tempo real no espaco. Conquiste planetas, construa torres, domine constelacoes.',
    url: '/spacewar/',
    status: 'beta',
    icone: '🚀',
    gradiente: 'linear-gradient(135deg, #1e3a8a 0%, #0a0f1a 100%)',
    capa: CAPAS['spacewar'],
    meta: [
      { icone: '⚡', texto: 'Tempo real' },
      { icone: '👥', texto: 'Solo + IA' },
    ],
  },
  {
    id: 'digitacao',
    titulo: 'Digitação',
    descricao:
      'Treinador completo de digitacao: 40 niveis do F J D K ao texto corrido, teclado que pinta o dedo certo, ranking, ligas e o Bubble Game.',
    url: 'https://ensinosocial.com.br/digitacao',
    status: 'estavel',
    icone: '⌨️',
    gradiente: 'linear-gradient(135deg, #1e88e5 0%, #0b2545 100%)',
    capa: CAPAS['digitacao'],
    meta: [
      { icone: '🎯', texto: '40 níveis' },
      { icone: '🏆', texto: 'Ranking e ligas' },
    ],
  },
  {
    id: 'encceja-praticar',
    titulo: 'Treino ENCCEJA',
    descricao:
      'Treinos de matematica ligados as competencias da matriz da prova. Pratique jogando exatamente a habilidade que o ENCCEJA cobra.',
    url: 'https://ensinosocial.com.br/encceja/praticar',
    status: 'estavel',
    icone: '🎯',
    gradiente: 'linear-gradient(135deg, #12b8a2 0%, #063d36 100%)',
    capa: CAPAS['encceja-praticar'],
    meta: [
      { icone: '📋', texto: 'Matriz do Inep' },
      { icone: '🎓', texto: 'Modo adulto' },
    ],
  },
  {
    id: 'programar',
    titulo: 'Oficina de Programação',
    descricao:
      'Aprenda a programar jogando: 8 jogos em trilha, do primeiro bloco de encaixar ao JavaScript puro. Labirinto, tartaruga, musica e os duelos da lagoa.',
    url: '/programar/',
    status: 'estavel',
    icone: '🧩',
    gradiente: 'linear-gradient(135deg, #6366f1 0%, #1e1b4b 100%)',
    capa: CAPAS['programar'],
    meta: [
      { icone: '🗺️', texto: '8 jogos em trilha' },
      { icone: '💻', texto: 'Do bloco ao JavaScript' },
    ],
  },
  {
    id: 'exercicios',
    titulo: 'Bancada de Exercícios',
    descricao:
      'Treinos de matematica com correcao na hora e resolucao passo a passo. Errou? A explicacao aparece. Acertou? Cada tentativa vem com numeros novos.',
    url: '/exercicios/',
    status: 'estavel',
    icone: '✏️',
    gradiente: 'linear-gradient(135deg, #12b8a2 0%, #04352e 100%)',
    capa: CAPAS['exercicios'],
    meta: [
      { icone: '✅', texto: 'Correção automática' },
      { icone: '🎓', texto: 'Matriz do ENCCEJA' },
    ],
  },
  {
    id: 'ferramentas',
    titulo: 'Ferramentas de Estudo',
    descricao:
      'Kit com 12 ferramentas grátis: ábaco, calculadora, treino de contas de cabeça, frações, gráficos, relógio, pomodoro e editor pros seus resumos.',
    url: '/ferramentas/',
    status: 'estavel',
    icone: '🧰',
    gradiente: 'linear-gradient(135deg, #fdc400 0%, #4a3800 100%)',
    capa: CAPAS['ferramentas'],
    meta: [
      { icone: '🧰', texto: '12 ferramentas' },
      { icone: '🍅', texto: 'Com timer de foco' },
    ],
  },

];
